import React, {useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import classnames from "classnames";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import Button from "../../../../../../components/base/button/button";
import AddressCardTaxInfoSectionV2 from "./tax-info-v2";
import AddressCardActionsPopoverV2, {AddressCardActionsV2 as AddressCardActions} from "./actions-popover-v2";
import {formatPhoneNumber} from "../../../../../../../core/services/utils/utils";
import useCountryProvinceCache from "../../../../../../hooks/use-country-province-cache";
import {Skeleton} from "@mui/material";

export {AddressCardActions};

const AddressCardV2 = ({
                           address,
                           remove,
                           edit,
                           editTaxes,
                           setDefault,
                           duplicate,
                           onClick,
                           disabled: disabledProps = false,
                           readonly = false,
                           excludeActions = [],
                           allowCreation = false,
                           allowRemoval = false,
                           allowSetDefault = false,
                           settingAsDefault = false,
                       }) => {
    const {countries} = useCountryProvinceCache(undefined, true);
    const [showTaxInfo, setShowTaxInfo] = useState(false);
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    const hideTaxInfo = useMemo(() => excludeActions?.includes(AddressCardActions.taxInfo), [excludeActions])

    /**@type {boolean}*/
    const disabled = useMemo(() => removing || disabledProps, [removing, disabledProps])

    const actions = useMemo(() => {
        const actions = Object.values(AddressCardActions);
        const excluded = Array.from(excludeActions ?? []);
        if (address?.isDefault || !allowSetDefault) {
            excluded.push(AddressCardActions.setDefault);
        }
        if (!allowRemoval) {
            excluded.push(AddressCardActions.remove);
        }
        if (!allowCreation) {
            excluded.push(AddressCardActions.duplicate);
        }

        return actions.filter(e => !excluded.includes(e));
    }, [excludeActions, address?.isDefault, allowSetDefault, allowRemoval, allowCreation])

    /**@type {string}*/
    const addressText = useMemo(() => {
        let res = '';
        if (!address)
            return res;

        if (address.name?.length) {
            res = res.concat(address.name)
            res = res.concat('<br/>')
        }
        if (address.line1?.length) {
            res = res.concat(address.line1)
            res = res.concat('<br/>')
        }
        if (address.line2?.length) {
            res = res.concat(address.line2)
            res = res.concat('<br/>')
        }
        if (address.line3?.length) {
            res = res.concat(address.line3)
            res = res.concat('<br/>')
        }
        if (address.line4?.length) {
            res = res.concat(address.line4)
            res = res.concat('<br/>')
        }

        if (address.city?.length) {
            res = res.concat(address.city)
            res = res.concat(', ')
        }
        if (address.provState?.length) {
            if (address.country?.length) {
                const country = countries?.find(c => c.code === address.country);
                const fullProvince = country?.provinces?.find(p => p.code === address.provState);
                res = res.concat(fullProvince?.name ?? address.provState)
            } else {
                res = res.concat(address.provState)
            }
            res = res.concat(', ')
        }
        if (address?.postalCode?.length) {
            res = res.concat(address.postalCode.toUpperCase())
            res = res.concat(', ')
        }
        if (address.country?.length) {
            const country = countries?.find(c => c.code === address.country);
            res = res.concat(country?.name ?? address.country)
        }
        res = res.concat('<br/>');

        if (address.phone?.length) {
            res = res.concat('Phone: ', formatPhoneNumber(address.phone), '<br/>')
        }
        if (address.email?.length) {
            res = res.concat('Email: ', address.email, '<br/>')
        }
        if (address.website?.length) {
            res = res.concat('Website: ', address.website, '<br/>')
        }
        if (address.fax?.length) {
            res = res.concat('Fax: ', formatPhoneNumber(address.fax), '<br/>')
        }

        return res;
    }, [address, countries])

    /**
     * Invokes the remove [callback] from the props.
     *
     * * sets the removing state value so that the ui can disable the applicable actions of this component.
     * @return {Promise<void>}
     */
    const removeAddress = async () => {
        setRemoving(true);
        await remove?.();
        if (!isMounted())
            return;
        setRemoving(false);
    }

    /**
     * Invokes the [onClick] callback of the props.
     *
     * * if [disabled] or [showTaxInfo] is set, it does nothing
     * * if [onClick] callback is not defined, does nothing.
     * @param {MouseEvent} e
     */
    const onAddressClicked = (e) => {
        if (disabled || showTaxInfo || !onClick)
            return;
        onClick(e);
    }

    return (
        <>
            <div
                onClick={onAddressClicked}
                className={classnames('address-card', {'clickable': !disabled && !showTaxInfo && !!onClick})}
            >
                <div className={'address-card-header'}>
                    {
                        showTaxInfo &&
                        <div className={'tax-info-title-section'}>
                            <IconButton onPressed={() => setShowTaxInfo(false)}>
                                <ArrowBack className='icon normal'/>
                            </IconButton>
                            <p className={'title'}>
                                Tax Information
                            </p>
                        </div>
                    }
                    {
                        !showTaxInfo &&
                        <div className={'address-title-section'}>
                            <p className={'title'}>
                                {address?.name ?? address?.title ?? ''}
                            </p>
                            <p className={'type'}>
                                {address?.type ?? ''}
                            </p>
                        </div>
                    }
                    <div className={'d-flex align-items-center justify-content-end'}>
                        {
                            !readonly && !!actions.length &&
                            <AddressCardActionsPopoverV2
                                actions={actions}
                                disabled={disabled}
                                edit={edit}
                                duplicate={duplicate}
                                setDefault={setDefault}
                                settingAsDefault={settingAsDefault}
                                {...!!remove ? {remove: removeAddress} : {}}
                            />
                        }
                    </div>
                </div>
                {
                    showTaxInfo &&
                    <AddressCardTaxInfoSectionV2
                        salesTaxes={address?.salesTaxes}
                        setShowTaxInfo={setShowTaxInfo}
                        edit={editTaxes}
                        readonly={readonly}
                    />
                }
                {
                    !showTaxInfo &&
                    <>
                        <div className={'address-card-content'}>
                            <p dangerouslySetInnerHTML={{__html: addressText}}/>
                        </div>
                        <div className={'address-card-footer'}>
                            <div>
                                {
                                    settingAsDefault
                                        ? <Skeleton sx={{transform: 'none'}}>
                                            <div className={'default-indicator'}>
                                                Default Shipping Address
                                            </div>
                                        </Skeleton>
                                        : address?.isDefault &&
                                        <div className={'default-indicator'}>
                                            Default Shipping Address
                                        </div>
                                }
                            </div>
                            {
                                !hideTaxInfo &&
                                <div>
                                    <Button
                                        style={{margin: '2px 0'}}
                                        disabled={disabled}
                                        variant={'text'}
                                        color={'primary'}
                                        onClick={() => setShowTaxInfo(true)}
                                    >
                                        Tax Info
                                    </Button>
                                </div>
                            }
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default AddressCardV2;
